import CustomDrawer from '@/components/CustomDrawer';
import TruncateText from '@/components/TruncateText';
import {
  AlertCircleIcon,
  CheckCircleIcon,
  DownloadIcon,
  FileTextIcon,
  UploadIcon,
  XIcon,
} from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import colors from '@/theme/colors';
import { Button, Flex, Form, Spin, Upload } from 'antd';
import { useState } from 'react';
import CustomImage from '../CustomImage';
import type { BulkUploaderProps, TStagedFile } from './BulkUploader.types';

const { Dragger } = Upload;

/**
 * A reusable bulk file uploader drawer component.
 *
 * Features:
 * - Drag-and-drop file selection with Ant Design Dragger
 * - Single file at a time (new file replaces the previous one)
 * - Two-phase flow: Stage locally → Upload on "Done" click
 * - Ant Design Form for validation
 * - Proper success/error banners and toast messaging
 * - Accepts a module-specific ErrorViewer via props
 */
export default function BulkUploader({
  open,
  onClose,
  title,
  subtitle = 'File Type Must be in .xlsx',
  accept = '.xlsx',
  onDownloadTemplate,
  onUpload,
  uploadLoading = false,
  errorViewer,
  onFileStaged,
  preview,
}: BulkUploaderProps) {
  const [form] = Form.useForm();
  const [stagedFile, setStagedFile] = useState<TStagedFile | null>(null);
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'uploading' | 'success' | 'error'
  >('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  /**
   * Handle file selection from the dragger.
   * Stores the file locally — does NOT trigger an API call.
   * Replaces the previous file if one was already staged.
   */
  const handleFileSelect = ({ file }: any) => {
    const actualFile: File = file.originFileObj || file;

    const newStagedFile: TStagedFile = {
      uid: file.uid || crypto.randomUUID(),
      name: actualFile.name,
      file: actualFile,
    };

    setStagedFile(newStagedFile);
    setUploadStatus('idle');
    setErrorMessage('');
    form.setFieldsValue({ file: newStagedFile });
    onFileStaged?.(actualFile);

    return false;
  };

  /**
   * Remove the staged file and reset form state.
   */
  const handleRemoveFile = () => {
    setStagedFile(null);
    setUploadStatus('idle');
    setErrorMessage('');
    form.setFieldsValue({ file: undefined });
    onFileStaged?.(null);
  };

  /**
   * Form submit handler — uploads the staged file via the provided callback.
   */
  const onFinish = async () => {
    if (!stagedFile) return;

    try {
      setUploadStatus('uploading');
      setErrorMessage('');

      await onUpload(stagedFile.file);

      setUploadStatus('success');
      toast('success', 'File uploaded successfully.');

      setStagedFile(null);
      form.resetFields();
      onFileStaged?.(null);
      onClose();
      setUploadStatus('idle');
    } catch (error) {
      setUploadStatus('error');
      setErrorMessage(
        getErrorMessage(error, 'Upload failed. Please try again.'),
      );
    }
  };

  /**
   * Close the drawer and reset all state.
   */
  const handleDrawerClose = () => {
    setStagedFile(null);
    setUploadStatus('idle');
    setErrorMessage('');
    form.resetFields();
    onFileStaged?.(null);
    onClose();
  };

  const isUploading = uploadStatus === 'uploading' || uploadLoading;

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleDrawerClose}
      title={title}
    >
      <Form form={form} onFinish={onFinish} layout="vertical">
        {/* Header Icon & Title */}
        <div className="flex flex-col items-center gap-3 py-6">
          {/* <div className="w-16 h-16 rounded-2xl bg-theme-primary/10 flex items-center justify-center">
            <CloudUploadIcon size={32} className="text-theme-primary" />
          </div> */}
          <CustomImage
            width={100}
            height={100}
            alt="Upload"
            src="/images/upload.svg"
            className="object-contain"
          />
          <div className="text-center">
            <h6 className="text-2xl font-bold text-white">Upload file</h6>
            <p className="text-base text-gray-400 mt-1">{subtitle}</p>
          </div>
        </div>

        {/* Drag & Drop Zone */}
        <Form.Item
          name="file"
          rules={[
            { required: true, message: 'Please select a file to upload' },
          ]}
          className="!mb-0"
        >
          <Dragger
            showUploadList={false}
            customRequest={handleFileSelect}
            accept={accept}
            maxCount={1}
            height={200}
            className="!border-none !bg-transparent"
            style={{
              borderWidth: 2,
              borderStyle: 'dashed',
              borderColor: colors.gray[700],
              backgroundColor: colors.slate[800],
              borderRadius: 12,
            }}
          >
            <div className="flex flex-col items-center gap-4 py-2">
              <div className="w-12 h-12 rounded-xl bg-theme-slate-700 flex items-center justify-center">
                <UploadIcon size={24} className="text-gray-400" />
              </div>
              <p className="text-white/70 text-base">
                Drag & drop your file here
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="text-theme-primary text-sm font-semibold underline underline-offset-2 cursor-pointer hover:text-theme-primary/80 transition-colors"
                >
                  Browse Files
                </button>
                <span className="text-gray-500 text-sm">or</span>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-theme-primary text-sm font-semibold underline underline-offset-2 cursor-pointer hover:text-theme-primary/80 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownloadTemplate();
                  }}
                >
                  <DownloadIcon size={14} />
                  Download Template
                </button>
              </div>
            </div>
          </Dragger>
        </Form.Item>

        {/* Staged File Card */}
        {stagedFile && (
          <div className="mt-5 rounded-xl border border-theme-gray-700 bg-theme-slate-800 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-theme-primary/10 flex items-center justify-center shrink-0">
                  <FileTextIcon size={20} className="text-theme-primary" />
                </div>
                <div className="min-w-0">
                  <TruncateText
                    text={stagedFile.name}
                    className="text-white font-medium text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">
                    {(stagedFile.file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <Button
                size="small"
                color="default"
                variant="text"
                className="rounded shrink-0"
                icon={<XIcon size={20} />}
                classNames={{ icon: 'flex items-center justify-center' }}
                onClick={handleRemoveFile}
              />
            </div>

            {/* Upload progress indicator */}
            {isUploading && (
              <div className="h-0.5 w-full bg-theme-gray-700 overflow-hidden">
                <div
                  className="h-full bg-theme-primary animate-pulse rounded-full"
                  style={{ width: '60%' }}
                />
              </div>
            )}
          </div>
        )}

        {/* What the staged file would do, when the module provides it */}
        {stagedFile && preview}

        {/* Status Banners */}
        {stagedFile && uploadStatus === 'idle' && (
          <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
            <CheckCircleIcon
              size={20}
              className="text-emerald-400 shrink-0 mt-0.5"
            />
            <div>
              <p className="text-emerald-400 font-semibold text-sm">
                File ready to upload
              </p>
              <p className="text-emerald-400/60 text-xs mt-0.5">
                Click "Done" to start uploading the file.
              </p>
            </div>
          </div>
        )}

        {isUploading && (
          <div className="mt-4 flex items-center gap-3 p-4 rounded-xl bg-theme-primary/5 border border-theme-primary/20">
            <Spin size="small" />
            <p className="text-theme-primary font-semibold text-sm">
              Uploading file...
            </p>
          </div>
        )}

        {uploadStatus === 'error' && errorMessage && (
          <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-red-500/5 border border-red-500/20">
            <AlertCircleIcon
              size={20}
              className="text-red-400 shrink-0 mt-0.5"
            />
            <div>
              <p className="text-red-400 font-semibold text-sm">
                Upload Failed
              </p>
              <p className="text-red-400/60 text-xs mt-0.5">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <Flex justify="end" align="center" gap={12} className="mt-8">
          {errorViewer}
          <Button
            size="middle"
            type="primary"
            htmlType="submit"
            disabled={!stagedFile || isUploading}
            loading={isUploading}
            className="!h-10 !px-6 !text-sm !font-semibold !rounded-lg !bg-theme-primary !border-theme-primary !text-[#1e293b] hover:!bg-theme-primary/90 disabled:!opacity-50 disabled:!cursor-not-allowed"
          >
            Done
          </Button>
        </Flex>
      </Form>
    </CustomDrawer>
  );
}
